import { Popover as PopoverPrimitive } from "@base-ui/react/popover"
import { format, isValid, parse, startOfDay } from "date-fns"
import { CalendarIcon } from "lucide-react"
import * as React from "react"

import { cn } from "@/lib/utils"

import { Calendar } from "./calendar"

interface DateFieldProps {
  id?: string
  value?: string
  onValueChange: (value: string | undefined) => void
  placeholder?: string
  disabled?: boolean
  minValue?: string
  maxValue?: string
  onBlur?: () => void
  ariaInvalid?: boolean
  ariaDescribedBy?: string
  ariaLabel?: string
  className?: string
  buttonClassName?: string
}

const dateOnlyFormat = "yyyy-MM-dd"

export function DateField({
  id,
  value,
  onValueChange,
  placeholder = "Select date",
  disabled = false,
  minValue,
  maxValue,
  onBlur,
  ariaInvalid,
  ariaDescribedBy,
  ariaLabel,
  className,
  buttonClassName,
}: DateFieldProps) {
  const [open, setOpen] = React.useState(false)
  const selectedDate = parseRosterDate(value)
  const minDate = parseRosterDate(minValue)
  const maxDate = parseRosterDate(maxValue)

  const disabledDays = React.useMemo(() => {
    if (!minDate && !maxDate) {
      return undefined
    }

    return (day: Date) => {
      const dayTime = normalizeDateOnly(day).getTime()
      const minTime = minDate ? normalizeDateOnly(minDate).getTime() : null
      const maxTime = maxDate ? normalizeDateOnly(maxDate).getTime() : null

      if (minTime !== null && dayTime < minTime) {
        return true
      }

      if (maxTime !== null && dayTime > maxTime) {
        return true
      }

      return false
    }
  }, [maxDate, minDate])

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
      <PopoverPrimitive.Trigger
        id={id}
        disabled={disabled}
        onBlur={onBlur}
        aria-invalid={ariaInvalid}
        aria-describedby={ariaDescribedBy}
        aria-label={ariaLabel}
        className={cn(
          "dark:bg-input/30 border-input focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:aria-invalid:border-destructive/50 disabled:bg-input/50 dark:disabled:bg-input/80 h-11 rounded-xl border bg-card/84 px-3.5 text-sm shadow-[inset_0_1px_0_var(--surface-ring)] transition-[border-color,box-shadow,background-color] outline-none focus-visible:ring-3 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 inline-flex w-full min-w-0 items-center justify-between gap-2",
          buttonClassName,
        )}
      >
        <span
          className={cn(
            "truncate",
            value ? "text-foreground" : "text-muted-foreground/90",
          )}
        >
          {selectedDate ? formatDisplayDate(selectedDate) : placeholder}
        </span>
        <CalendarIcon className="size-4 shrink-0 text-muted-foreground" />
      </PopoverPrimitive.Trigger>

      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Positioner sideOffset={6} className="z-50">
          <PopoverPrimitive.Popup
            className={cn(
              "bg-popover text-popover-foreground data-open:animate-in data-closed:animate-out data-closed:fade-out-0 data-open:fade-in-0 data-closed:zoom-out-95 data-open:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 w-fit origin-(--transform-origin) rounded-2xl border border-border/60 shadow-[0_24px_45px_-30px_color-mix(in_oklch,var(--foreground)_40%,transparent)] ring-1 ring-primary/10 duration-100",
              className,
            )}
          >
            <Calendar
              mode="single"
              selected={selectedDate}
              disabled={disabledDays}
              onSelect={(nextDate) => {
                onValueChange(nextDate ? toRosterDate(nextDate) : undefined)
                setOpen(false)
              }}
              className="p-2"
            />
          </PopoverPrimitive.Popup>
        </PopoverPrimitive.Positioner>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  )
}

function parseRosterDate(value?: string) {
  if (!value) {
    return undefined
  }

  const parsed = parse(value, dateOnlyFormat, new Date())
  if (!isValid(parsed) || format(parsed, dateOnlyFormat) !== value) {
    return undefined
  }

  return parsed
}

function toRosterDate(value: Date) {
  return format(value, dateOnlyFormat)
}

function formatDisplayDate(value: Date) {
  return value.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  })
}

function normalizeDateOnly(value: Date) {
  return startOfDay(value)
}
